import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShieldCheck, Stethoscope, Smile, Layers3, HeartHandshake } from "lucide-react";
import { TID } from "@/lib/testIds";
import { FEELINGS } from "@/lib/feelings";

export default function Landing() {
  return (
    <div className="space-y-16">
      <section className="relative overflow-hidden rounded-3xl border border-[var(--sw-line)] sw-hero-wash sw-grain">
        <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center px-6 py-12 md:px-12 md:py-20">
          <div>
            <div className="inline-flex items-center gap-2 sw-pill text-xs uppercase tracking-widest">
              <ShieldCheck size={14} /> For OTs · Educators · School districts
            </div>
            <h1 data-testid={TID.landingTitle} className="display mt-5 text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight text-[var(--sw-primary-700)]">
              Feelings, meet <span className="text-[var(--sw-amber-700)]">strategies that work.</span>
            </h1>
            <div className="sw-spectrum mt-5" aria-hidden />
            <p className="mt-5 text-lg md:text-xl text-[var(--sw-ink-soft)] max-w-xl">
              SenseWise pairs a warm, feelings-first check-in with eight OT-grounded sensory boxes — grounded in
              Ayres Sensory Integration and Dunn&apos;s Sensory Processing Model. Built for students,
              their teachers, and the therapists who support them.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid={TID.exploreTryNow} to="/explore" className="sw-pill sw-cta-warm h-14 px-7 text-lg">
                <Sparkles size={18} /> Try it now
              </Link>
              <Link data-testid={TID.landingTherapist} to="/login" className="sw-pill h-14 px-7 text-lg">
                <Stethoscope size={18} /> Therapist sign-in
              </Link>
              <Link data-testid={TID.landingStudent} to="/student-login" className="sw-pill h-14 px-7 text-lg">
                <Smile size={18} /> I&apos;m a student
              </Link>
            </div>
            <div className="mt-5 text-xs text-[var(--sw-ink-soft)]">
              District pilot? Email <a href="mailto:Hello@occuthera.com" className="underline text-[var(--sw-primary)] font-semibold">Hello@occuthera.com</a> for a signed DPA.
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="grid grid-cols-2 gap-3">
            {FEELINGS.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.key}
                  data-testid={`landing-feeling-tile-${f.key}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  className="rounded-2xl p-3 sm:p-4 pr-4 sm:pr-5 border overflow-hidden"
                  style={{ background: f.bg, borderColor: f.border }}
                >
                  <Icon size={26} style={{ color: f.color }} />
                  <div
                    data-testid={`landing-feeling-prompt-${f.key}`}
                    className="display font-bold mt-2 leading-tight text-sm sm:text-base md:text-lg tracking-tight whitespace-nowrap"
                    style={{ color: f.color }}
                  >
                    {f.prompt}
                  </div>
                  <div className="text-[11px] font-semibold mt-0.5" style={{ color: f.color, opacity: 0.8 }}>
                    {f.subtitle}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-6">
        {[
          { icon: HeartHandshake, title: "Feelings-first", body: "Start with what's happening inside — anger, fear, overwhelm, sadness — and get strategies matched to that feeling.", tint: "#EF6E42", tintBg: "#FDDDCE" },
          { icon: Layers3, title: "Or by sensory system", body: "Eight OT-grounded boxes (Proprioceptive, Vestibular, Tactile, Visual, Auditory, Interoceptive, Oral, Olfactory) for browsing by body input.", tint: "#2A5AA0", tintBg: "#DDE8F8" },
          { icon: ShieldCheck, title: "Clinical when you need it", body: "Try the whole thing free with no login. When an OT sets up a curated set for you, every selection is logged to inform their notes.", tint: "#22857B", tintBg: "#D5F0EC" },
        ].map(({ icon: Icon, title, body, tint, tintBg }, i) => (
          <div key={i} className="sw-card sw-card-spectrum p-6">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl" style={{ background: tintBg, color: tint }}>
              <Icon size={22} />
            </div>
            <h3 className="display text-xl font-bold mt-4">{title}</h3>
            <p className="mt-2 text-[var(--sw-ink-soft)]">{body}</p>
          </div>
        ))}
      </section>

      {/* District trust band */}
      <section className="rounded-3xl border border-[var(--sw-line)] bg-white p-6 md:p-10">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">For school districts</div>
            <h2 className="display mt-2 text-3xl md:text-4xl font-extrabold text-[var(--sw-primary-700)] leading-tight">
              Clinical-grade. Privacy-first. Ready to scale.
            </h2>
            <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[var(--sw-ink-soft)]">
              <li className="flex gap-2"><ShieldCheck size={16} className="text-[var(--sw-primary)] shrink-0 mt-0.5" /> FERPA-aware data handling; district DPAs available.</li>
              <li className="flex gap-2"><ShieldCheck size={16} className="text-[var(--sw-primary)] shrink-0 mt-0.5" /> Silent, respectful logging — never gamified or ranked.</li>
              <li className="flex gap-2"><ShieldCheck size={16} className="text-[var(--sw-primary)] shrink-0 mt-0.5" /> Bulk student roster import for OTs across schools.</li>
              <li className="flex gap-2"><ShieldCheck size={16} className="text-[var(--sw-primary)] shrink-0 mt-0.5" /> Accessibility target: WCAG 2.1 AA + reduced motion.</li>
              <li className="flex gap-2"><ShieldCheck size={16} className="text-[var(--sw-primary)] shrink-0 mt-0.5" /> No advertising, no third-party trackers, no data sale.</li>
              <li className="flex gap-2"><ShieldCheck size={16} className="text-[var(--sw-primary)] shrink-0 mt-0.5" /> PDF selection reports for IEP / MTSS documentation.</li>
            </ul>
          </div>
          <div className="flex flex-col gap-3">
            <a href="mailto:Hello@occuthera.com?subject=SenseWise%20District%20Pilot" className="sw-pill sw-cta-warm h-12 px-6 justify-center" data-testid="landing-district-cta">
              Request a district pilot
            </a>
            <Link to="/evidence" className="sw-pill h-12 px-6 justify-center">
              See the evidence base
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
